// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import { lowerCase } from 'src/utils/stringFormatter';
import { Programme, ProgrammeActivityType, ProgrammeType } from 'src/resources/api/programme/types.api';
import { NavListProps } from './types';
import { FeatureTypes } from 'src/@types/feature';
import { getProgrammeName } from 'src/utils/get-programme-name';

// ----------------------------------------------------------------------

const generateRouteChildren = (el: Programme) => {
  const children: { [key: string]: NavListProps[] } = {
    CUSTOMER: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Communicate', path: PATH_DASHBOARD.programs.communicate(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Engagement', path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code) },
    ],
    MANAGED: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      {
        title: 'Calculate',
        path: PATH_DASHBOARD.programs.footprints(lowerCase(el.type), el.code),
      },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Communicate', path: PATH_DASHBOARD.programs.communicate(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Engagement', path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code) },
    ],
    MYCARBONCLICK: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Participants', path: PATH_DASHBOARD.programs.participants(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Communicate', path: PATH_DASHBOARD.programs.communicate(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Engagement', path: PATH_DASHBOARD.programs.engagement(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
  }

  let result = children[el?.type];

  if (el?.type === ProgrammeType.MYCARBONCLICK) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.FLIGHT) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.JOURNEY) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.NONE) {
    return result?.filter((item) => item?.title !== 'Calculate' && item?.title !== 'Insights');
  }

  return result?.filter((item) => item?.title !== 'Insights');
}

export interface ProgrammeMenu {
  title: string;
  code: string;
  path: string;
  id: string;
  programme: any;
  hideDatePicker?: boolean;
  programmeMenu: NavListProps[];
}

export const getProgramMenu = (programmes: Programme[]) => {
  const menu: ProgrammeMenu[] = programmes?.map((el) => {
    const children = generateRouteChildren(el);
    const name = getProgrammeName(el?.name);

    return {
      title: name === 'Mycarbonclick' ? 'My carbonclick' : name,
      code: el.code,
      path: PATH_DASHBOARD.programs.program(lowerCase(el.type), el.code),
      defaultPath: children[0].path,
      id: `nav-item-programme-${el.code}`,
      programme: el.type,
      programmeMenu: children || [],
      isProgramme: true,
    }
  });

  return menu;
};

//

export enum TopNavigationItems {
  programmes = 'programmes',
  resources = 'resources',
  reporting = 'reporting',
}

export enum NavItemAction {
  guidance = 'guidance',
}


const navConfig = [
  {
    title: 'Programmes',
    name: TopNavigationItems.programmes,
    description: "Where the work happens. Open a programme to calculate a footprint, buy offsets against it, and set up what your customers see.",
    path: '#',
    children: [
      // { title: 'Business customers', featureCode: FeatureTypes.MANAGE_BUSINESS_CUSTOMER, path: PATH_DASHBOARD.businessCustomers, id: 'nav-item-business-customers', icon: <IconBuilding />, subtitle: 'Manage businesses that have participated in your programmes. Profiles may be added manually or created automatically.' },
    ],
  },
  {
    title: 'Reporting',
    name: TopNavigationItems.reporting,
    description: "Your numbers, across every programme. What you have offset, every purchase behind it, and who took part.",
    path: '#',
    children: [
      { title: 'Impact', path: PATH_DASHBOARD.offsets.impact },
      { title: 'Transactions', path: PATH_DASHBOARD.offsets.transactions },
      { title: 'Individuals', path: PATH_DASHBOARD.individuals },
      // Note: Old and not relevant now. Needs to be migrated
      // { title: 'My footprints', featureCode: FeatureTypes.FOOTPRINT, path: PATH_DASHBOARD.offsets.footprints, id: 'nav-item-my-footprints', icon: <IconCalculator />, subtitle: 'Calculate the carbon footprint of and manage your business emissions – from flights, freight, fuel use and more.' },
      { title: 'Business customers', featureCode: FeatureTypes.MANAGE_BUSINESS_CUSTOMER, path: PATH_DASHBOARD.businessCustomers },
    ],
  },
  {
    title: 'Resources',
    name: TopNavigationItems.resources,
    description: "What your programmes draw on. The projects and portfolios you offset into, the guidance and articles behind them, and the tools you use to reach customers.",
    path: '#',
    children: [
      { title: 'Projects', path: PATH_DASHBOARD.projects.root,  },
      { title: 'Portfolios', path: PATH_DASHBOARD.baskets.root },
      { title: 'Landing pages', featureCode: FeatureTypes.LANDING_PAGE, path: PATH_DASHBOARD.resources.landingPages },
      { title: 'Integrations', featureCode: FeatureTypes.INTERACTIVE_TOOLS, path: PATH_DASHBOARD.toolkit.root },
      { title: 'Guidance', path: '#', action: NavItemAction.guidance },
      { title: 'Articles', path: "https://www.carbonclick.com/news-views" },
    ],
  },
  {
    title: 'Settings',
    name: 'settings',
    hideOnDesktop: true,
    path: '#',
    children: [
      { title: 'General', path: PATH_DASHBOARD.settings.business },
      { title: 'Theme', path: PATH_DASHBOARD.settings.theme },
      { title: 'Default portfolio', path: PATH_DASHBOARD.settings.defaultBasket },
      { title: 'Billing', path: PATH_DASHBOARD.settings.billing },
      { title: 'Billing receipts', path: PATH_DASHBOARD.settings.emails },
    ],
  }
];

export const getNavConfig = (
  programmes: Programme[] = [],
  merchantFeatures?: { code: string }[],
  packageCode?: string
) => {
  const programmeMenuItems = getProgramMenu(programmes);

  const menuWithProgrammes = navConfig.map((section) => {
    if (section.name === 'programmes') {
      return {
        ...section,
        children: [...section.children, ...programmeMenuItems],
      };
    }
    return section;
  })

  const newMenu = menuWithProgrammes.map((section) => {
    const updatedSection = {
      ...section,
      children: section.children
        ?.filter((item: any) => item?.action !== NavItemAction.guidance || !!packageCode)
        .map((item: any) => {
          return {
            ...item,
            disabled: !item?.featureCode ? false : !merchantFeatures?.find(feature => feature?.code === item?.featureCode)
          }
        }),
    };
    
    return updatedSection;
  })

  return newMenu;
}

export default navConfig;
